import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  ChevronLeft, ChevronRight, CalendarDays, MapPin, Phone,
  Check, X, Link2, Plus
} from "lucide-react";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

export default function CalendarPage() {
  const [searchParams] = useSearchParams();
  const [currentMonth, setCurrentMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  });
  const [bookings, setBookings] = useState([]);
  const [leads, setLeads] = useState([]);
  const [showNewBooking, setShowNewBooking] = useState(false);
  const [selectedDate, setSelectedDate] = useState("");
  const [newBooking, setNewBooking] = useState({ lead_id: "", date: "", notes: "" });
  const [gcalConnected, setGcalConnected] = useState(false);

  const fetchBookings = useCallback(async () => {
    try {
      const { data } = await api.get(`/bookings?month=${currentMonth}`);
      setBookings(data);
    } catch (err) { console.error("Failed to load bookings:", err); }
  }, [currentMonth]);

  const fetchLeads = useCallback(async () => {
    try {
      const { data } = await api.get("/leads");
      setLeads(data.filter(l => ["approved", "scheduled"].includes(l.stage)));
    } catch (err) { console.error("Failed to load leads:", err); }
  }, []);

  const checkGcal = useCallback(async () => {
    try {
      const { data } = await api.get("/oauth/calendar/status");
      setGcalConnected(data.connected);
    } catch (err) { console.error("Failed to check Google Calendar status:", err); }
  }, []);

  useEffect(() => { fetchBookings(); fetchLeads(); checkGcal(); }, [fetchBookings, fetchLeads, checkGcal]);

  useEffect(() => {
    if (searchParams.get("connected") === "true") {
      toast.success("Google Calendar connected!");
      setGcalConnected(true);
    }
    if (searchParams.get("error")) toast.error("Google Calendar connection failed");
  }, [searchParams]);

  const connectGoogleCalendar = async () => {
    try {
      const { data } = await api.get("/oauth/calendar/login");
      window.location.href = data.authorization_url;
    } catch { toast.error("Failed to start Google Calendar connection"); }
  };

  const handleCreateBooking = async (e) => {
    e.preventDefault();
    if (!newBooking.lead_id || !newBooking.date) { toast.error("Select a lead and date"); return; }
    try {
      await api.post("/bookings", newBooking);
      toast.success("Job scheduled!");
      setShowNewBooking(false);
      setNewBooking({ lead_id: "", date: "", notes: "" });
      fetchBookings();
      fetchLeads();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to book"); }
  };

  const handleConfirm = async (bookingId) => {
    try {
      await api.put(`/bookings/${bookingId}/confirm`);
      toast.success("Booking confirmed! SMS sent to customer.");
      fetchBookings();
    } catch { toast.error("Failed to confirm"); }
  };

  const handleCancel = async (bookingId) => {
    try {
      await api.put(`/bookings/${bookingId}/cancel`);
      toast.success("Booking cancelled");
      fetchBookings();
    } catch { toast.error("Failed to cancel"); }
  };

  // Calendar grid
  const [year, month] = currentMonth.split("-").map(Number);
  const firstDay = new Date(year, month - 1, 1);
  const lastDay = new Date(year, month, 0);
  const startPad = (firstDay.getDay() + 6) % 7; // Monday start
  const totalDays = lastDay.getDate();
  const today = new Date().toISOString().split("T")[0];

  const getBookingsForDate = (date) => bookings.filter(b => b.date === date);

  const prevMonth = () => {
    const d = new Date(year, month - 2, 1);
    setCurrentMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  };
  const nextMonth = () => {
    const d = new Date(year, month, 1);
    setCurrentMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  };

  const pendingBookings = bookings.filter(b => b.status === "requested");

  return (
    <div className="space-y-6" data-testid="calendar-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-zinc-900 tracking-tight">Calendar</h1>
          <p className="text-sm text-zinc-500 mt-1">Schedule and manage installations (1 job per day)</p>
        </div>
        <div className="flex gap-2">
          {!gcalConnected ? (
            <Button variant="outline" onClick={connectGoogleCalendar} data-testid="connect-gcal-btn">
              <Link2 className="w-4 h-4 mr-2" /> Connect Google Calendar
            </Button>
          ) : (
            <Badge className="bg-green-100 text-green-700 h-9 px-3 flex items-center">Google Calendar Connected</Badge>
          )}
          <Button onClick={() => setShowNewBooking(true)} className="bg-orange-600 hover:bg-orange-700 text-white" data-testid="schedule-job-btn">
            <Plus className="w-4 h-4 mr-2" /> Schedule Job
          </Button>
        </div>
      </div>

      {/* Pending booking requests */}
      {pendingBookings.length > 0 && (
        <Card className="border border-amber-300 bg-amber-50/50 shadow-none" data-testid="pending-bookings">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-bold text-amber-800">Pending Date Requests ({pendingBookings.length})</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {pendingBookings.map(b => (
              <div key={b.id} className="flex items-center justify-between bg-white border border-amber-200 rounded-lg p-3">
                <div>
                  <p className="text-sm font-semibold text-zinc-900">{b.lead_name}</p>
                  <p className="text-xs text-zinc-500">Requested: {b.preferred_dates?.join(", ") || b.date}</p>
                  {b.notes && <p className="text-xs text-zinc-400 mt-0.5">{b.notes}</p>}
                </div>
                <div className="flex gap-1">
                  <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => handleConfirm(b.id)} data-testid={`confirm-booking-${b.id}`}>
                    <Check className="w-3.5 h-3.5 mr-1" /> Confirm
                  </Button>
                  <Button size="sm" variant="outline" className="h-8" onClick={() => handleCancel(b.id)}>
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Calendar Grid */}
      <Card className="border border-zinc-200 shadow-none">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <Button variant="ghost" size="sm" onClick={prevMonth}><ChevronLeft className="w-5 h-5" /></Button>
            <h2 className="text-lg font-bold text-zinc-900">{MONTHS[month - 1]} {year}</h2>
            <Button variant="ghost" size="sm" onClick={nextMonth}><ChevronRight className="w-5 h-5" /></Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Day headers */}
          <div className="grid grid-cols-7 gap-px mb-1">
            {DAYS.map(d => <div key={d} className="text-center text-xs font-semibold text-zinc-500 py-1">{d}</div>)}
          </div>
          {/* Calendar cells */}
          <div className="grid grid-cols-7 gap-px">
            {Array.from({ length: startPad }).map((_, i) => <div key={`pad-${i}`} className="min-h-[80px] bg-zinc-50 rounded" />)}
            {Array.from({ length: totalDays }).map((_, i) => {
              const day = i + 1;
              const dateStr = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
              const dayBookings = getBookingsForDate(dateStr);
              const isToday = dateStr === today;
              const isPast = dateStr < today;
              const isWeekend = new Date(year, month - 1, day).getDay() % 6 === 0;

              return (
                <div
                  key={day}
                  className={`min-h-[80px] p-1 rounded border transition-colors cursor-pointer
                    ${isToday ? "border-orange-400 bg-orange-50/50" : "border-zinc-100 hover:border-orange-300"}
                    ${isPast ? "opacity-50" : ""} ${isWeekend ? "bg-zinc-50" : "bg-white"}`}
                  onClick={() => { if (!isPast && !isWeekend) { setNewBooking(prev => ({ ...prev, date: dateStr })); setSelectedDate(dateStr); setShowNewBooking(true); }}}
                  data-testid={`cal-day-${dateStr}`}
                >
                  <div className="flex items-center justify-between">
                    <span className={`text-xs font-medium ${isToday ? "text-orange-600 font-bold" : "text-zinc-600"}`}>{day}</span>
                    {dayBookings.length > 0 && <div className="w-2 h-2 rounded-full bg-orange-500" />}
                  </div>
                  {dayBookings.map(b => (
                    <div key={b.id} className={`mt-1 text-[10px] px-1 py-0.5 rounded truncate ${b.status === "confirmed" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                      {b.lead_name || "Job"}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Upcoming Jobs List */}
      <Card className="border border-zinc-200 shadow-none">
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-bold">Upcoming Jobs</CardTitle>
        </CardHeader>
        <CardContent>
          {bookings.filter(b => b.status === "confirmed" && b.date >= today).length === 0 ? (
            <p className="text-sm text-zinc-400 text-center py-6">No upcoming jobs scheduled</p>
          ) : (
            <div className="space-y-2">
              {bookings.filter(b => b.status === "confirmed" && b.date >= today).map(b => (
                <div key={b.id} className="flex items-center justify-between p-3 border border-zinc-100 rounded-lg" data-testid={`upcoming-${b.id}`}>
                  <div className="flex items-center gap-3">
                    <div className="bg-orange-100 rounded-lg p-2 text-center min-w-[48px]">
                      <p className="text-xs text-orange-600 font-bold">{new Date(b.date + "T12:00:00").toLocaleDateString("en-US", { month: "short" })}</p>
                      <p className="text-lg font-extrabold text-orange-700">{new Date(b.date + "T12:00:00").getDate()}</p>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-zinc-900">{b.lead_name}</p>
                      {b.lead_address && <p className="text-xs text-zinc-500 flex items-center gap-1"><MapPin className="w-3 h-3" /> {b.lead_address}</p>}
                      {b.lead_phone && <p className="text-xs text-zinc-500 flex items-center gap-1"><Phone className="w-3 h-3" /> {b.lead_phone}</p>}
                    </div>
                  </div>
                  <Button size="sm" variant="outline" className="text-xs text-red-500 border-red-200 hover:bg-red-50" onClick={() => handleCancel(b.id)}>
                    Cancel
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* New Booking Dialog */}
      <Dialog open={showNewBooking} onOpenChange={setShowNewBooking}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="text-lg font-bold">Schedule Installation</DialogTitle></DialogHeader>
          <form onSubmit={handleCreateBooking} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium">Customer</Label>
              <Select value={newBooking.lead_id} onValueChange={(v) => setNewBooking({ ...newBooking, lead_id: v })}>
                <SelectTrigger data-testid="booking-lead-select"><SelectValue placeholder="Select approved lead" /></SelectTrigger>
                <SelectContent>
                  {leads.map(l => <SelectItem key={l.id} value={l.id}>{l.name} - {l.address || l.phone}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium">Installation Date</Label>
              <Input type="date" value={newBooking.date} onChange={(e) => setNewBooking({ ...newBooking, date: e.target.value })} min={today} data-testid="booking-date" />
            </div>
            <div className="space-y-2">
              <Label className="text-sm font-medium">Notes</Label>
              <Textarea value={newBooking.notes} onChange={(e) => setNewBooking({ ...newBooking, notes: e.target.value })} placeholder="Job notes..." rows={2} data-testid="booking-notes" />
            </div>
            <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={() => setShowNewBooking(false)} className="flex-1">Cancel</Button>
              <Button type="submit" className="flex-1 bg-orange-600 hover:bg-orange-700 text-white" data-testid="confirm-schedule-btn">Schedule</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
